import { Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";
import { SITE, waLink } from "@/data/site";

const NAV = [
  { label: "Koleksiyonlar", href: "#katalog" },
  { label: "Çok Satanlar", href: "#cok-satanlar" },
  { label: "Seçki", href: "#secki" },
  { label: "Hikâyemiz", page: "/hikayemiz" },
  { label: "Kategoriler", href: "#kategoriler" },
  { label: "Teknik Servis", page: "/teknik-servis" },
];

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (l) => {
    if (l.page) {
      navigate(l.page);
      window.scrollTo(0, 0);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(l.href), 500);
    } else {
      scrollToId(l.href);
    }
  };

  return (
  <footer data-testid="editorial-footer" className="relative bg-emerald-deep text-ivory overflow-hidden">
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-ivory/20 to-transparent" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-20 pb-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="font-serif text-3xl font-semibold">
            Atılmaz <span className="italic text-gold">Saat</span>
          </p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.3em] text-ivory/40">
            {SITE.slogan}
          </p>
          <p className="mt-5 max-w-sm text-sm leading-relaxed text-ivory/55 font-light">
            2006'dan bu yana kol saati, takı ve gözlükte güvenilir adres.
            Orijinallik garantisi ve uzman teknik servisimizle yanınızdayız.
          </p>
          <div className="mt-6 flex gap-3">
            <a
              href={`https://instagram.com/${SITE.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="footer-instagram-link"
              className="h-11 w-11 rounded-full border border-ivory/15 inline-flex items-center justify-center text-ivory/70 transition-colors duration-300 hover:border-ivory/50 hover:text-ivory"
            >
              <Instagram size={17} />
            </a>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="footer-facebook-link"
              className="h-11 w-11 rounded-full border border-ivory/15 inline-flex items-center justify-center text-ivory/70 transition-colors duration-300 hover:border-ivory/50 hover:text-ivory"
            >
              <Facebook size={17} />
            </a>
          </div>
        </div>

        <div className="lg:col-span-3">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ivory/40">Mağaza</p>
          <ul className="mt-5 space-y-3">
            {NAV.map((l) => (
              <li key={l.label}>
                <button
                  onClick={() => go(l)}
                  data-testid={`footer-link-${(l.page || l.href).replace(/^[#/]+/, "")}`}
                  className="text-sm text-ivory/65 hover:text-ivory transition-colors"
                >
                  {l.label}
                </button>
              </li>
            ))}
            <li>
              <a
                href={SITE.shopifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-shopify-link"
                className="text-sm text-ivory/65 hover:text-ivory transition-colors"
              >
                Online Mağaza (atilmazsaat.com)
              </a>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ivory/40">İletişim</p>
          <ul className="mt-5 space-y-4 text-sm text-ivory/65">
            <li className="flex items-start gap-3">
              <MapPin size={15} className="text-ivory/50 mt-0.5 shrink-0" />
              <span>{SITE.address}</span>
            </li>
            <li>
              <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} data-testid="footer-phone-link" className="flex items-center gap-3 hover:text-ivory transition-colors">
                <Phone size={15} className="text-ivory/50 shrink-0" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${SITE.email}`} data-testid="footer-email-link" className="flex items-center gap-3 hover:text-ivory transition-colors">
                <Mail size={15} className="text-ivory/50 shrink-0" /> {SITE.email}
              </a>
            </li>
            <li>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="inline-flex items-center gap-2 rounded-full bg-ivory px-5 h-10 text-[13px] font-semibold text-emerald transition-colors hover:bg-sand"
              >
                WhatsApp Sipariş Hattı
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 overflow-hidden pointer-events-none select-none">
        <p className="font-serif text-[7vw] leading-[0.9] font-semibold text-outline-ivory whitespace-nowrap text-center">
          Zamana Değer Katar
        </p>
      </div>

      <div className="mt-8 pt-6 border-t border-ivory/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-ivory/40">
        <p data-testid="footer-copyright">© {new Date().getFullYear()} Atılmaz Saat — Tüm hakları saklıdır.</p>
        <p>www.atilmazsaat.com</p>
      </div>
    </div>
  </footer>
  );
};
